import * as Sentry from '@sentry/react';
import { onCLS, onLCP, onTTFB, onINP } from 'web-vitals';

export function initializeMonitoring() {
  const dsn = import.meta.env.VITE_SENTRY_DSN;

  if (dsn && import.meta.env.MODE === 'production') {
    Sentry.init({
      dsn,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration({
          maskAllText: true,
          blockAllMedia: true,
        }),
      ],
      tracesSampleRate: 0.1,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      environment: import.meta.env.MODE,
      beforeSend(event, hint) {
        if (import.meta.env.MODE === 'development') {
          console.error('Sentry Event:', event);
          console.error('Original Error:', hint.originalException);
          return null;
        }
        return event;
      },
    });
  }
}

export function setUserContext(user: { id: string; email?: string; role?: string }) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser({
      id: user.id,
      email: user.email,
      role: user.role,
    });
  }
}

export function setTenantContext(tenant: { id: string; name: string }) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setContext('tenant', {
      id: tenant.id,
      name: tenant.name,
    });
  }
}

export function clearUserContext() {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setUser(null);
  }
}

export function captureException(error: Error, context?: Record<string, unknown>) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureException(error, {
      extra: context,
    });
  } else {
    console.error('Error:', error, context);
  }
}

export function captureMessage(message: string, level: Sentry.SeverityLevel = 'info') {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.captureMessage(message, level);
  }
}

function sendToAnalytics(metric: { name: string; value: number; rating: string }) {
  if (import.meta.env.VITE_SENTRY_DSN) {
    Sentry.setMeasurement(metric.name, metric.value, 'millisecond');
  }

  if (import.meta.env.MODE === 'development') {
    console.log('Performance Metric:', metric);
  }
}

export function initializePerformanceMonitoring() {
  onCLS((metric) => {
    sendToAnalytics({
      name: 'CLS',
      value: metric.value,
      rating: metric.rating,
    });
  });

  onLCP((metric) => {
    sendToAnalytics({
      name: 'LCP',
      value: metric.value,
      rating: metric.rating,
    });
  });

  onTTFB((metric) => {
    sendToAnalytics({
      name: 'TTFB',
      value: metric.value,
      rating: metric.rating,
    });
  });

  onINP((metric) => {
    sendToAnalytics({
      name: 'INP',
      value: metric.value,
      rating: metric.rating,
    });
  });
}
